import { useEffect, useCallback, useRef } from 'react';

interface PerformanceOptimizerProps {
  enableCodeSplitting?: boolean;
  enableTaskScheduling?: boolean;
  enableResourceHints?: boolean;
  enableUnusedCodeRemoval?: boolean;
}

const PerformanceOptimizer: React.FC<PerformanceOptimizerProps> = ({
  enableCodeSplitting = true,
  enableTaskScheduling = true,
  enableResourceHints = true,
  enableUnusedCodeRemoval = true
}) => {
  const taskQueue = useRef<Array<() => void>>([]);
  const isProcessingTasks = useRef(false);
  const performanceMetrics = useRef({
    fcp: 0,
    lcp: 0,
    cls: 0,
    fid: 0,
    tbt: 0
  });

  // Task scheduler to break up long-running operations
  const scheduleTask = useCallback((task: () => void, priority: 'high' | 'low' = 'low') => {
    if (priority === 'high') {
      taskQueue.current.unshift(task);
    } else {
      taskQueue.current.push(task);
    }
    
    if (!isProcessingTasks.current) {
      processTaskQueue();
    }
  }, []);

  // Process task queue using scheduler API or setTimeout fallback
  const processTaskQueue = useCallback(() => {
    if (taskQueue.current.length === 0) {
      isProcessingTasks.current = false;
      return;
    }

    isProcessingTasks.current = true;

    const processChunk = () => {
      const startTime = performance.now();
      
      // Process tasks for max 5ms to prevent blocking
      while (taskQueue.current.length > 0 && (performance.now() - startTime) < 5) {
        const task = taskQueue.current.shift();
        try {
          task?.();
        } catch (error) {
          // Task execution failed silently in production
        }
      }

      if (taskQueue.current.length > 0) {
        // Use scheduler API if available, otherwise setTimeout
        if ('scheduler' in window && 'postTask' in (window as any).scheduler) {
          (window as any).scheduler.postTask(processChunk, { priority: 'background' });
        } else {
          setTimeout(processChunk, 0);
        }
      } else {
        isProcessingTasks.current = false;
      }
    };

    processChunk();
  }, []);

  // Dynamically load non-critical components with mobile-first approach
  const loadNonCriticalComponents = useCallback(() => {
    if (!enableCodeSplitting) return;

    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const isSlowConnection = typeof navigator !== 'undefined' && 
      (navigator as any).connection && 
      ((navigator as any).connection.effectiveType === 'slow-2g' || (navigator as any).connection.effectiveType === '2g');

    // Mobile: much more aggressive deferring
    const deferTime = isMobile ? (isSlowConnection ? 5000 : 3000) : 1000;

    // Load heavy components only when needed
    const loadComponentsOnInteraction = () => {
      const heavyComponents = [
        'components-chat',
        'components-3d', 
        'components-animations',
        'page-chat',
        'page-blog',
        'ai-services',
        'three-js'
      ];

      // Mobile: only load on explicit user action, not passive scroll
      const loadOnInteraction = () => {
        heavyComponents.forEach(component => {
          scheduleTask(() => {
            // Component loaded on demand
          }, 'low');
        });

        // Remove listeners after first interaction
        const events = isMobile ? ['click', 'touchstart'] : ['click', 'scroll', 'keydown', 'touchstart'];
        events.forEach(eventType => {
          document.removeEventListener(eventType, loadOnInteraction);
        });
      };

      // Mobile: more conservative event listeners
      const events = isMobile ? ['click', 'touchstart'] : ['click', 'scroll', 'keydown', 'touchstart'];
      events.forEach(eventType => {
        document.addEventListener(eventType, loadOnInteraction, { passive: true, once: true } as AddEventListenerOptions);
      });
    };

    // Delay non-critical component loading more aggressively on mobile
    setTimeout(loadComponentsOnInteraction, deferTime);
  }, [enableCodeSplitting, scheduleTask]);

  // Remove unused event listeners and clean up DOM with mobile focus
  const cleanupUnusedCode = useCallback(() => {
    if (!enableUnusedCodeRemoval) return;

    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    scheduleTask(() => {
      // Mobile: more aggressive cleanup
      if (isMobile) {
        // Remove all non-essential event listeners on mobile
        const allElements = document.querySelectorAll('*');
        allElements.forEach(element => {
          // Remove data attributes that are not needed on mobile
          if (element.hasAttribute('data-temp')) element.removeAttribute('data-temp');
          if (element.hasAttribute('data-desktop-only')) element.remove();
        });

        // Remove desktop-specific DOM nodes
        const desktopOnlyElements = document.querySelectorAll('.desktop-only, [data-desktop-only]');
        desktopOnlyElements.forEach(element => element.remove());

        // Reduce DOM complexity on mobile
        const complexElements = document.querySelectorAll('[class*="animate-"]:not(.animate-spin)');
        complexElements.forEach(element => {
          element.classList.forEach(className => {
            if (className.startsWith('animate-') && className !== 'animate-spin') {
              element.classList.remove(className);
            }
          });
        });
      }

      // Standard cleanup for all devices
      const unusedListeners = document.querySelectorAll('[data-unused-listener]');
      unusedListeners.forEach(element => {
        element.removeAttribute('data-unused-listener');
      });

      const orphanedNodes = document.querySelectorAll('[data-component-unmounted]');
      orphanedNodes.forEach(node => node.remove());

      // Mobile: aggressive CSS class cleanup
      if (isMobile) {
        const elementsWithManyClasses = document.querySelectorAll('[class]');
        elementsWithManyClasses.forEach(element => {
          const classList = element.classList;
          if (classList.length > 15) { // Lower threshold for mobile
            // Element with many CSS classes detected (production logging disabled)
          }
        });
      }
    }, 'low');
  }, [enableUnusedCodeRemoval, scheduleTask]);

  // Add resource hints with mobile-specific priorities
  const addResourceHints = useCallback(() => {
    if (!enableResourceHints) return;

    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    scheduleTask(() => {
      // Mobile: fewer preconnects to reduce network overhead
      const domains = isMobile ? [
        'https://fonts.googleapis.com',
        'https://fonts.gstatic.com'
      ] : [
        'https://fonts.googleapis.com',
        'https://fonts.gstatic.com',
        'https://api.github.com'
      ];

      domains.forEach(domain => {
        if (!document.querySelector(`link[href="${domain}"]`)) {
          const link = document.createElement('link');
          link.rel = 'preconnect';
          link.href = domain;
          if (!isMobile) link.crossOrigin = 'anonymous';
          document.head.appendChild(link);
        }
      });

      // Mobile: skip DNS prefetch for non-essential domains
      if (!isMobile) {
        const prefetchDomains = [
          '//www.linkedin.com',
          '//github.com',
          '//stackoverflow.com'
        ];

        prefetchDomains.forEach(domain => {
          if (!document.querySelector(`link[href="${domain}"]`)) {
            const link = document.createElement('link');
            link.rel = 'dns-prefetch';
            link.href = domain;
            document.head.appendChild(link);
          }
        });
      }
    }, isMobile ? 'low' : 'high');
  }, [enableResourceHints, scheduleTask]);

  // Monitor and optimize long tasks with mobile-specific thresholds
  const monitorLongTasks = useCallback(() => {
    if (!enableTaskScheduling) return;

    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const mobileThreshold = 30; // Lower threshold for mobile
    const desktopThreshold = 50;
    const threshold = isMobile ? mobileThreshold : desktopThreshold;

    // Long Task API monitoring with mobile-specific handling
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const longTasks = list.getEntries();
        longTasks.forEach(task => {
          if (task.duration > threshold) {
            // Long task detected (production logging disabled)
            performanceMetrics.current.tbt += task.duration - threshold;
          }
        });
      });

      observer.observe({ entryTypes: ['longtask'] });
    }

    // Break up synchronous operations more aggressively on mobile
    const optimizeLongOperations = () => {
      scheduleTask(() => {
        const allElements = document.querySelectorAll('*');
        const chunkSize = isMobile ? 50 : 100; // Smaller chunks for mobile
        
        for (let i = 0; i < allElements.length; i += chunkSize) {
          scheduleTask(() => {
            const chunk = Array.from(allElements).slice(i, i + chunkSize);
            chunk.forEach(element => {
              // Mobile: simpler processing
              if (element.hasAttribute('data-temp')) {
                element.removeAttribute('data-temp');
              }
              
              // Mobile: remove complex CSS classes
              if (isMobile && element.classList.length > 10) {
                const unnecessaryClasses = ['transition-all', 'duration-300', 'ease-in-out'];
                unnecessaryClasses.forEach(className => {
                  element.classList.remove(className);
                });
              }
            });
          }, 'low');
        }
      }, 'low');
    };

    // Defer heavy operations longer on mobile
    setTimeout(optimizeLongOperations, isMobile ? 4000 : 2000);
  }, [enableTaskScheduling, scheduleTask]);

  // Monitor Core Web Vitals
  const monitorWebVitals = useCallback(() => {
    if (!('PerformanceObserver' in window)) return;

    // LCP monitoring
    const lcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];
      performanceMetrics.current.lcp = lastEntry.startTime;
      
      if (lastEntry.startTime > 2500) {
        // LCP is slow (production logging disabled)
      }
    });
    lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

    // FCP monitoring
    const fcpObserver = new PerformanceObserver((entryList) => {
      entryList.getEntries().forEach(entry => {
        if (entry.name === 'first-contentful-paint') {
          performanceMetrics.current.fcp = entry.startTime;
          
          if (entry.startTime > 1800) {
            // FCP is slow (production logging disabled)
          }
        }
      });
    });
    fcpObserver.observe({ entryTypes: ['paint'] });

    // CLS monitoring
    const clsObserver = new PerformanceObserver((entryList) => {
      let clsValue = 0;
      entryList.getEntries().forEach(entry => {
        if (!(entry as any).hadRecentInput) {
          clsValue += (entry as any).value;
        }
      });
      performanceMetrics.current.cls = clsValue;
      
      if (clsValue > 0.1) {
        // CLS is high (production logging disabled)
      }
    });
    clsObserver.observe({ entryTypes: ['layout-shift'] });

    // FID monitoring (deprecated, but keeping for compatibility)
    const fidObserver = new PerformanceObserver((entryList) => {
      entryList.getEntries().forEach(entry => {
        performanceMetrics.current.fid = (entry as any).processingStart - entry.startTime;
        
        if (performanceMetrics.current.fid > 100) {
          // FID is high (production logging disabled)
        }
      });
    });
    fidObserver.observe({ entryTypes: ['first-input'] });
  }, []);

  // Optimize animations and reduce reflows with mobile-first approach
  const optimizeAnimations = useCallback(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    scheduleTask(() => {
      // Mobile: disable most animations for performance
      if (isMobile) {
        const animatedElements = document.querySelectorAll('[class*="animate-"]:not(.animate-spin)');
        animatedElements.forEach(element => {
          element.classList.forEach(className => {
            if (className.startsWith('animate-') && className !== 'animate-spin') {
              element.classList.remove(className);
            }
          });
        });

        // Mobile: remove will-change to free up GPU memory
        const willChangeElements = document.querySelectorAll('[style*="will-change"]');
        willChangeElements.forEach(element => {
          (element as HTMLElement).style.willChange = 'auto';
        });

        return; // Skip other animation optimizations on mobile
      }

      // Desktop: standard animation optimizations
      const animatedElements = document.querySelectorAll('[class*="animate-"], [style*="transform"], [style*="transition"]');
      
      animatedElements.forEach(element => {
        const computedStyle = window.getComputedStyle(element);
        
        if (computedStyle.transform !== 'none' && !computedStyle.willChange.includes('transform')) {
          (element as HTMLElement).style.willChange = 'transform';
        }
        
        if (computedStyle.left !== 'auto' || computedStyle.top !== 'auto') {
          // Element animating layout properties (production logging disabled)
        }
      });

      // Desktop: optimize scroll handlers
      const optimizeScrollHandlers = () => {
        let ticking = false;
        
        const optimizedScrollHandler = () => {
          if (!ticking) {
            requestAnimationFrame(() => {
              ticking = false;
            });
            ticking = true;
          }
        };

        document.addEventListener('scroll', optimizedScrollHandler, { passive: true } as AddEventListenerOptions);
      };

      optimizeScrollHandlers();
    }, isMobile ? 'low' : 'high');
  }, [scheduleTask]);

  // Cleanup function for component unmount
  const cleanup = useCallback(() => {
    taskQueue.current = [];
    isProcessingTasks.current = false;
  }, []);

  // Initialize optimizations
  useEffect(() => {
    // Wait for initial render to complete
    const initOptimizations = () => {
      scheduleTask(() => loadNonCriticalComponents(), 'low');
      scheduleTask(() => addResourceHints(), 'low');
      scheduleTask(() => monitorLongTasks(), 'low');
      scheduleTask(() => monitorWebVitals(), 'high');
      scheduleTask(() => optimizeAnimations(), 'low');
      
      // Clean up unused code after initial load
      setTimeout(() => {
        scheduleTask(() => cleanupUnusedCode(), 'low');
      }, 5000);
    };

    // Start optimizations after a brief delay
    setTimeout(initOptimizations, 100);

    return cleanup;
  }, [
    loadNonCriticalComponents,
    addResourceHints,
    monitorLongTasks,
    monitorWebVitals,
    optimizeAnimations,
    cleanupUnusedCode,
    cleanup,
    scheduleTask
  ]);

  // Provide global performance utilities
  useEffect(() => {
    // Add global performance utilities
    (window as any).scheduleTask = scheduleTask;
    (window as any).getPerformanceMetrics = () => performanceMetrics.current;

    return () => {
      delete (window as any).scheduleTask;
      delete (window as any).getPerformanceMetrics;
    };
  }, [scheduleTask]);

  // This component doesn't render anything visible
  return null;
};

export default PerformanceOptimizer; 